import { normalizePhoneNumber } from 'features/call/call-base/helpers';
import type { HearingParticipant } from 'features/call/call-hearing/interfaces';
import type { TranscriptSpeakerRole } from 'features/voice-meeting/interfaces';

export type CaptionSpeakerColorVariant =
  | 'local'
  | 'primaryHearing'
  | 'additionalHearing1'
  | 'additionalHearing2'
  | 'unknown';

const ADDITIONAL_HEARING_COLOR_VARIANTS = [
  'additionalHearing1',
  'additionalHearing2',
] as const satisfies readonly CaptionSpeakerColorVariant[];

const INTERPRETER_LABEL_PATTERN = /^Interpreter\b/;

export const buildCaptionSpeakerColorMap = (
  hearings: HearingParticipant[]
): Map<string, CaptionSpeakerColorVariant> => {
  const colorMap = new Map<string, CaptionSpeakerColorVariant>();
  const primaryHearing = hearings[0];

  const primaryPhoneNumber = primaryHearing?.phoneNumber?.trim() ?? '';

  if (primaryPhoneNumber.length > 0) {
    colorMap.set(normalizePhoneNumber(primaryPhoneNumber), 'primaryHearing');
  }

  const additionalHearings = hearings
    .slice(1)
    .filter((hearing) => (hearing.phoneNumber?.trim() ?? '').length > 0);

  additionalHearings.forEach((hearing, index) => {
    const variant = ADDITIONAL_HEARING_COLOR_VARIANTS[index];

    if (!variant) {
      return;
    }

    colorMap.set(normalizePhoneNumber(hearing.phoneNumber), variant);
  });

  return colorMap;
};

interface ResolveCaptionSpeakerColorVariantParams {
  speakerRole: TranscriptSpeakerRole;
  speakerLabel: string;
  speakerColorMap: Map<string, CaptionSpeakerColorVariant>;
}

export const resolveCaptionSpeakerColorVariant = ({
  speakerRole,
  speakerLabel,
  speakerColorMap,
}: ResolveCaptionSpeakerColorVariantParams): CaptionSpeakerColorVariant => {
  if (speakerRole === 'local') {
    return 'local';
  }

  if (INTERPRETER_LABEL_PATTERN.test(speakerLabel.trim())) {
    return 'local';
  }

  const normalizedLabel = normalizePhoneNumber(speakerLabel.trim());

  if (normalizedLabel.length > 0) {
    const mappedVariant = speakerColorMap.get(normalizedLabel);

    if (mappedVariant) {
      return mappedVariant;
    }
  }

  if (speakerRole === 'remote') {
    return 'primaryHearing';
  }

  return 'unknown';
};
